import React, { type ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { type AvatarData, useGetAuthProfile, useGetMyProfile } from '@/api';
import { useUser } from '@/lib/auth';
import { ProfileEditButton, ProfileShareButton } from '@/module/profile/share/UserProfile/ProfileActionButton';
import { ProfileAvatar } from '@/shared/features/user-avatar';
import { type PlanType } from '@/types';
import { OfficialQuizChip, PlanSection, ProfileContainer, ProfileContent } from './ProfileWidget';
type UserProfileProps = {
  userHashId: string;
  name: ReactNode;
  email: string | null;
  isAmbassador: boolean;
  avatarData?: AvatarData | null;
  planType?: PlanType;
};
export const MyProfile = () => {
  const {
    user
  } = useUser();
  const {
    data
  } = useGetAuthProfile({
    query: {
      // 기존 유저 정보가 없는 경우가 있어서 새롭게 데이터를 불러옵니다.
      enabled: !user?.quizUserId
    }
  });
  const {
    data: myProfile
  } = useGetMyProfile({
    query: {
      enabled: !!user
    }
  });
  const quizUserId = user?.quizUserId ?? data?.data.user.quizUserId ?? '';
  const avatarData = myProfile?.data?.avatarData;
  if (!user) return null;
  // 학생은 플랜을 구매하는 주체가 아니라 업그레이드 유도 띠를 노출하지 않는다.
  const isStudent = user.type?.toLowerCase() === 'student';
  return <ProfileContainer data-sentry-element="ProfileContainer" data-sentry-component="MyProfile" data-sentry-source-file="UserProfile.tsx">
      {!isStudent && <PlanSection planType={user.planType} planDuration={user.planDuration} />}
      {/* 플랜 섹션이 없으면 위의 패딩값으로 보정한다 (아래 UserProfile 과 동일) */}
      <ProfileContent className={isStudent ? 'pt-lg tablet-sm:pt-lg' : undefined} data-sentry-element="ProfileContent" data-sentry-source-file="UserProfile.tsx">
        <div className="flex min-w-0 flex-1 flex-col gap-md tablet-sm:flex-row tablet-sm:items-center">
          <ProfileAvatar avatarData={avatarData} className="shrink-0" data-sentry-element="ProfileAvatar" data-sentry-source-file="UserProfile.tsx" />
          <ProfileUserInfo userHashId={quizUserId} isAmbassador={user.isAmbassador} name={user.username} email={user.email ?? ''} data-sentry-element="ProfileUserInfo" data-sentry-source-file="UserProfile.tsx" />
        </div>
        <div className={'flex items-center gap-xs bg-transparent'}>
          <ProfileEditButton data-sentry-element="ProfileEditButton" data-sentry-source-file="UserProfile.tsx" />
          <ProfileShareButton event={{
          location: 'myQuiz'
        }} key={'share'} userHashId={quizUserId} data-sentry-element="ProfileShareButton" data-sentry-source-file="UserProfile.tsx" />
        </div>
      </ProfileContent>
    </ProfileContainer>;
};
export const UserProfile = (user: UserProfileProps) => {
  const {
    t
  } = useTranslation(['common']);
  const userName = t('common:profile.byUserQuiz', {
    name: user.name
  });
  return <ProfileContainer data-sentry-element="ProfileContainer" data-sentry-component="UserProfile" data-sentry-source-file="UserProfile.tsx">
      {/* 플랜 섹션이없어서 위의 패딩값으로 보정한다 */}
      <ProfileContent className={'pt-lg tablet-sm:pt-lg'} data-sentry-element="ProfileContent" data-sentry-source-file="UserProfile.tsx">
        <div className="flex min-w-0 flex-1 flex-col gap-md tablet-sm:flex-row tablet-sm:items-center">
          <ProfileAvatar avatarData={user.avatarData} className="shrink-0" data-sentry-element="ProfileAvatar" data-sentry-source-file="UserProfile.tsx" />
          <ProfileUserInfo {...user} name={userName} data-sentry-element="ProfileUserInfo" data-sentry-source-file="UserProfile.tsx" />
        </div>
        <div className={'flex items-center gap-xs bg-transparent'}>
          <ProfileShareButton event={{
          location: 'user',
          userEmail: user.email ?? '',
          username: String(user.name)
        }} userHashId={user.userHashId} key={'share'} data-sentry-element="ProfileShareButton" data-sentry-source-file="UserProfile.tsx" />
        </div>
      </ProfileContent>
    </ProfileContainer>;
};
const ProfileUserInfo = (props: UserProfileProps) => {
  return <div data-sentry-component="ProfileUserInfo" data-sentry-source-file="UserProfile.tsx">
      <div className={'flex flex-col gap-2xs'}>
        <div className={'flex items-center gap-2xs'}>
          <div className={'w-full text-heading-lg-extrabold text-default'}>
            {props.name}
          </div>
          {props.isAmbassador && <OfficialQuizChip className={'shrink-0 max-tablet-sm:hidden'} />}
        </div>
        <span className={'text-body-md-medium text-alternative'}>
          {props.email}
        </span>
        {props.isAmbassador && <OfficialQuizChip className={'my-xs tablet-sm:hidden'} />}
      </div>
    </div>;
};